import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const EmployerContext = createContext();

const STORAGE_KEY = 'healthcues_employers';
const TEMP_KEY = 'healthcues_unsaved';

// Default employer overrides (matches system defaults)
const getDefaultOverrides = (config) => ({
  waitingPeriod: config?.employerPlanRules?.waiting_period_days || 60,
  minWeeklyHours: config?.employerPlanRules?.minimum_weekly_hours || 20,
  eligibleClasses: {
    fulltime_w2: config?.employerPlanRules?.eligible_classes?.full_time_w2 !== false,
    parttime_w2: config?.employerPlanRules?.eligible_classes?.part_time_w2 !== false,
    seasonal_temp: config?.employerPlanRules?.eligible_classes?.seasonal_temp || false,
    union_cba: config?.employerPlanRules?.eligible_classes?.union_cba || false,
  },
  localMinWage: config?.employerPlanRules?.state_local_min_wage_override || 7.25,
  availablePlanTiers: config?.planTiers?.map(t => t.label) || ['1500', '1200', '1050', '900', '750', '600'],
});

const getDefaultAnalysisControls = (config) => ({
  payFrequency: config?.engineSettings?.payPeriodsPerYear || 12,
  optimizationStrategy: config?.engineSettings?.optimizationStrategy || 'employer',
  minEmployeeNet: config?.engineSettings?.minEmployeeNetBenefit || 0,
  includeFuta: config?.engineSettings?.includeFutaSavings || false,
});

export function EmployerProvider({ children, config }) {
  const [currentEmployer, setCurrentEmployer] = useState(null);
  const [employers, setEmployers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [managementViewOpen, setManagementViewOpen] = useState(false);
  
  // Load employers from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setEmployers(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load employers:', e);
    }
  }, []);
  
  // Save employers to localStorage whenever they change
  useEffect(() => {
    if (employers.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(employers));
      } catch (e) {
        console.error('Failed to save employers:', e);
      }
    }
  }, [employers]);
  
  // Auto-save temp state
  useEffect(() => {
    if (currentEmployer && !currentEmployer.id) {
      // This is unsaved work - save to temp
      try {
        localStorage.setItem(TEMP_KEY, JSON.stringify(currentEmployer));
      } catch (e) {
        console.error('Failed to save temp state:', e);
      }
    }
  }, [currentEmployer]);
  
  // Load temp state on mount if exists
  useEffect(() => {
    if (!currentEmployer) {
      try {
        const temp = localStorage.getItem(TEMP_KEY);
        if (temp) {
          setCurrentEmployer(JSON.parse(temp));
          setHasUnsavedChanges(true);
        }
      } catch (e) {
        console.error('Failed to load temp state:', e);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Create new employer (empty state)
  const createNewEmployer = useCallback(() => {
    setCurrentEmployer({
      id: null, // Will be assigned on save
      employerName: '',
      createdAt: null,
      updatedAt: null,
      notes: '',
      status: 'prospect',
      employees: [],
      originalCsvText: '',
      overrides: getDefaultOverrides(config),
      analysisControls: getDefaultAnalysisControls(config),
      summary: null,
    });
    setHasUnsavedChanges(false);
    localStorage.removeItem(TEMP_KEY);
  }, [config]);
  
  // Clear unsaved analysis and reset to null state
  const clearUnsaved = useCallback(() => {
    localStorage.removeItem(TEMP_KEY);
    setCurrentEmployer(null);
    setHasUnsavedChanges(false);
  }, []);
  
  // Update current employer
  const updateCurrentEmployer = useCallback((updates) => {
    setCurrentEmployer(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      // Mark as having changes if it's an existing employer
      if (prev.id) {
        setHasUnsavedChanges(true);
      }
      return updated;
    });
  }, []);
  
  // Update overrides
  const updateOverrides = useCallback((overrideUpdates) => {
    setCurrentEmployer(prev => {
      if (!prev) return null;
      return {
        ...prev,
        overrides: { ...prev.overrides, ...overrideUpdates },
      };
    });
    setHasUnsavedChanges(true);
  }, []);
  
  // Update analysis controls
  const updateAnalysisControls = useCallback((controlUpdates) => {
    setCurrentEmployer(prev => {
      if (!prev) return null;
      return {
        ...prev,
        analysisControls: { ...prev.analysisControls, ...controlUpdates },
      };
    });
    setHasUnsavedChanges(true);
  }, []);
  
  // Save employer (create or update)
  const saveEmployer = useCallback((employerData) => {
    const now = new Date().toISOString();
    const isNew = !employerData.id;
    
    const employer = {
      ...employerData,
      id: employerData.id || uuidv4(),
      createdAt: employerData.createdAt || now,
      updatedAt: now,
    };
    
    setEmployers(prev => {
      if (isNew) {
        return [employer, ...prev];
      } else {
        return prev.map(e => e.id === employer.id ? employer : e);
      }
    });
    
    setCurrentEmployer(employer);
    setHasUnsavedChanges(false);
    localStorage.removeItem(TEMP_KEY);
    
    return employer;
  }, []);
  
  // Load employer by ID
  const loadEmployer = useCallback(async (employerId) => {
    // First check if it's in the local employers array
    const employer = employers.find(e => e.id === employerId);
    if (employer) {
      setCurrentEmployer(employer);
      setHasUnsavedChanges(false);
      localStorage.removeItem(TEMP_KEY);
      return employer;
    }
    
    // If not found locally, try loading from clients (for client drawer uploads)
    try {
      const response = await fetch(`${API_URL}/api/clients/${employerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const client = await response.json();
        // Convert client format to employer format
        if (client.employees && client.employees.length > 0) {
          const employerData = {
            id: client._id || client.id,
            employerName: client.company?.legalName || 'Unknown',
            employees: client.employees,
            originalCsvText: client.originalCsvText || '',
            overrides: client.analysisConfig?.overrides || {},
            status: client.status || 'active'
          };
          setCurrentEmployer(employerData);
          setHasUnsavedChanges(false);
          localStorage.removeItem(TEMP_KEY);
          return employerData;
        }
      }
    } catch (error) {
      console.error('Error loading client as employer:', error);
    }
    
    return null;
  }, [employers]);
  
  // Delete employer
  const deleteEmployer = useCallback((employerId) => {
    setEmployers(prev => prev.filter(e => e.id !== employerId));
    if (currentEmployer?.id === employerId) {
      createNewEmployer();
    }
  }, [currentEmployer, createNewEmployer]);
  
  // Duplicate employer
  const duplicateEmployer = useCallback((employerId) => {
    const original = employers.find(e => e.id === employerId);
    if (!original) return null;
    
    const duplicate = {
      ...original,
      id: uuidv4(),
      employerName: `${original.employerName} (copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'prospect',
    };
    
    setEmployers(prev => [duplicate, ...prev]);
    return duplicate;
  }, [employers]);
  
  // Get portfolio summary
  const getPortfolioSummary = useCallback(() => {
    const summary = {
      totalEmployers: employers.length,
      totalEmployees: 0,
      totalEligible: 0,
      totalIneligible: 0,
      totalWaiting: 0,
      totalNotRecommended: 0,
      annualEmployerFica: 0,
      annualEmployeeBenefit: 0,
      annualHcRevenue: 0,
      byStatus: {
        prospect: 0,
        proposal: 0,
        active: 0,
        closed: 0,
      },
    };
    
    employers.forEach(emp => {
      if (emp.summary) {
        summary.totalEmployees += emp.summary.totalEmployees || 0;
        summary.totalEligible += emp.summary.eligible || 0;
        summary.totalIneligible += emp.summary.ineligible || 0;
        summary.totalWaiting += emp.summary.waiting || 0;
        summary.totalNotRecommended += emp.summary.notRecommended || 0;
        summary.annualEmployerFica += emp.summary.annualEmployerFica || 0;
        summary.annualEmployeeBenefit += emp.summary.annualEmployeeBenefit || 0;
        summary.annualHcRevenue += emp.summary.annualHcRevenue || 0;
      }
      if (emp.status && summary.byStatus[emp.status] !== undefined) {
        summary.byStatus[emp.status]++;
      }
    });
    
    return summary;
  }, [employers]);
  
  // Apply preset to overrides
  const applyPreset = useCallback((presetName) => {
    let preset = {};
    
    switch (presetName) {
      case 'maximum':
        preset = {
          waitingPeriod: 0,
          minWeeklyHours: 0,
          eligibleClasses: {
            fulltime_w2: true,
            parttime_w2: true,
            seasonal_temp: true,
            union_cba: true,
          },
          localMinWage: config?.employerPlanRules?.state_local_min_wage_override || 7.25,
          availablePlanTiers: config?.planTiers?.map(t => t.label) || ['1500', '1200', '1050', '900', '750', '600'],
        };
        break;
      case 'conservative':
        preset = {
          waitingPeriod: 90,
          minWeeklyHours: 30,
          eligibleClasses: {
            fulltime_w2: true,
            parttime_w2: false,
            seasonal_temp: false,
            union_cba: false,
          },
          localMinWage: config?.employerPlanRules?.state_local_min_wage_override || 7.25,
          availablePlanTiers: config?.planTiers?.map(t => t.label) || ['1500', '1200', '1050', '900', '750', '600'],
        };
        break;
      case 'standard':
      default:
        preset = getDefaultOverrides(config);
        break;
    }
    
    updateOverrides(preset);
  }, [config, updateOverrides]);
  
  const value = {
    currentEmployer,
    employers,
    hasUnsavedChanges,
    managementViewOpen,
    setManagementViewOpen,
    createNewEmployer,
    clearUnsaved,
    updateCurrentEmployer,
    updateOverrides,
    updateAnalysisControls,
    saveEmployer,
    loadEmployer,
    deleteEmployer,
    duplicateEmployer,
    getPortfolioSummary,
    applyPreset,
    setHasUnsavedChanges,
  };
  
  return (
    <EmployerContext.Provider value={value}>
      {children}
    </EmployerContext.Provider>
  );
}

export function useEmployer() {
  const context = useContext(EmployerContext);
  if (!context) {
    throw new Error('useEmployer must be used within EmployerProvider');
  }
  return context;
}
